import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import Api from '../../services/api';
import {
    Button,
    Divider,
    Flex,
    Typography,
    Select,
    Space,
    Spin,
    Row,
    Col,
    Card,
    Tooltip,
    Modal,
    DatePicker,
    notification,
    Empty,
    Alert,
    Result
} from 'antd';
import {
    LoadingOutlined,
    DownloadOutlined,
    DeleteOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { Centered } from '../../components';

export default function Decisions() {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [decisionBatches, setDecisionBatches] = useState([]);
    const [processModalOpen, setProcessModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [targetMonths, setTargetMonths] = useState([]);
    const [processDefenderIds, setProcessDefenderIds] = useState(['Todos']);
    const [defenders, setDefenders] = useState([]);
    const [responseStatusError, setResponseStatusError] = useState(null);

    const [api, contextHolder] = notification.useNotification({
        placement: 'top',
        maxCount: 1
    });
    
    /**
     * Recupera a lista de defensores.
     */
    const fetchDefenders = async () => {
        try {

            const response = await Api('defenders', { noPrefix: true }).index();
    
            setDefenders(response.data);
        } catch (error) {
            setResponseStatusError(error.status);
        }
    }

    /**
     * Recupera a lista de decisões já processadas.
     */
    async function fetchDecisionBatches() {
        try {
            const response = await Api('decisions').index();

            setDecisionBatches(response.data);
        } catch (error) {
            setResponseStatusError(error.status);
        }

        setIsFetching(false);
    }

    useEffect(() => {
        document.title = 'DPEMT - Decisões';

        fetchDecisionBatches();
        fetchDefenders();
    }, []);

    if (responseStatusError) {
        return (
            <Centered>
                <Result
                    status={responseStatusError}
                    subTitle={
                        <Typography.Title level={4}>
                            Não foi possível carregar este recurso.
                        </Typography.Title>
                    }
                    extra={
                        <Typography.Text>
                            Se acredita ser um erro, entre em contato com <a href="mailto:suportesistemas@dp.mt.gov.br">suportesistemas@dp.mt.gov.br</a>
                        </Typography.Text>
                    }
                />
            </Centered>
        );
    }

    /**
     * Submete a requisição para iniciar o processamento
     * das decisões.
     */
    async function createBatchProcessing() {
        setIsSubmitting(true);
        
        try {
            const response = await Api('decisions').store({
                target_months: targetMonths,
                defender_ids: processDefenderIds.filter(value => value !== 'Todos')
            });

            if (response.status === 202) {
                setDecisionBatches(previousBatches => [
                    response.data,
                    ...previousBatches
                ])

                setProcessModalOpen(false);
                setTargetMonths([]);
    
                api.success({
                    message: 'Compilação solicitada',
                    description: 'Dentro de alguns minutos o compilado de decisões ficará disponível.',
                });
            }

        } catch {
            api.error({
                message: 'Não foi possível solicitar a compilação das decisões.'
            });
        }

        setIsSubmitting(false);
    }

    /**
     * Submete a requisição para excluir o lote
     * de decisões especificado.
     * 
     * @param {Object} targetBatch 
     */
    async function deleteBatch(targetBatch) {
        const targetDateRef = targetBatch.date_ref.replace('-', '/');

        try {
            const response = await Api('decisions').delete(targetBatch.id);

            if (response.status === 200) {
                setDecisionBatches(previousBatches => {
                    return previousBatches.filter(batch => batch.id !== targetBatch.id);
                });

                api.success({
                    message: 'Compilado excluido',
                    description: `O compilado com as decisões de ${targetDateRef} foi apagado.`
                });

                setDeleteModalOpen(false);
            }
        } catch {
            api.error({
                message: `Não foi possível apagar o compilado com as decisões de ${targetDateRef}.`
            });
        }
    }

    if (isFetching) {
        return (
            <Centered>
                <Space direction="vertical" align="center" size="large">
                    <Spin indicator={<LoadingOutlined spin />} size="large" />

                    <Typography.Title level={5}>
                        Em alguns instantes os compilados de decisões aparecerão aqui.
                    </Typography.Title>
                </Space>
            </Centered>
        );
    }

     /**
     * Formata string de meses para exibição
     * no card de compilação    
     * 
     * @param {string} stringMonths - String com meses separados por vírgula (MM-YYYY)
     * @returns {string} Meses formatados para exibição
     */
    function formatMonths(stringMonths) {
        const months = stringMonths.split(',').map(item => item.trim().replace('-', '/'));

        if (months.length <= 3) 
            return months.join(', ');
      
        const firstMonths = months.slice(0, 3).join(', ');
        const finalMonths = months.length - 3;
        return `${firstMonths} + ${finalMonths} ${finalMonths > 1 ? 'meses' : 'mês'}`;      
    }

    return (
        <>
            {contextHolder}

            <Flex justify="space-between" align="center">
                <div>
                    <Typography.Title level={2} style={{ margin: 0 }}>Decisões</Typography.Title>
                    <Typography.Text style={{ marginTop: 0 }}>As decisões compiladas por mês de atividade.</Typography.Text>
                </div>

                <Button
                    type="primary"
                    icon={<SettingOutlined />}
                    onClick={() => setProcessModalOpen(true)}
                    onCancel={() => setProcessModalOpen(false)}
                >
                    Compilar Decisões
                </Button>
            </Flex>

            <Divider style={{ margin: '.5rem 0 .9rem 0' }} />

            <Row gutter={5} style={{ marginBottom: 12 }} justify="center">
                {decisionBatches.length ? decisionBatches.map(batch => {
                    const actions = batch.queued ? [] : [
                        <Tooltip title="Baixar decisões">
                            <DownloadOutlined
                                onClick={() => {
                                    window.open(`${process.env.REACT_APP_API_URL}/api/v1/renew-attachment/MonthlyDecision/${batch.id}`, '_blank')
                                }}                    
                            />                       
                        </Tooltip>,

                        <Tooltip title="Apagar compilado">
                            <DeleteOutlined
                                style={{ color: '#fc5656' }}
                                onClick={() => setDeleteModalOpen(batch)}
                            />
                        </Tooltip>
                    ]

                    return (
                        <Col key={batch.id}>
                            <Card
                                data-batch-id={batch.id}
                                actions={actions}
                                style={{
                                    width: 500,
                                    minHeight: 211.75,
                                    marginBottom: 8
                                }}
                                loading={batch.queued}
                                title={(
                                    <>
                                        <Typography.Title
                                            style={{
                                                marginBottom: batch.queued ? -8 : 0                                
                                            }}
                                            level={5}
                                        >
                                            Decisões de {formatMonths(batch.date_ref)}
                                        </Typography.Title>

                                        {batch.queued && (
                                            <Typography.Text type="secondary">
                                                <small>Em processamento</small>
                                            </Typography.Text>
                                        )}
                                    </>
                                )}
                            >
                                {!batch.queued && (
                                    <Card.Meta
                                        description={
                                            <>
                                                <Typography>Contém: <b>{batch.report_count} decisões</b></Typography>
                                                <Typography>Solicitado por: <b>{batch.user.full_name}</b></Typography>
                                                <Typography>Solicitado em: <b>{dayjs(batch.created_at).format('DD/MM/YYYY')}</b></Typography>
                                            </>
                                        }
                                    />
                                )}
                            </Card>
                        </Col>
                    );
                }) : (
                    <Centered height={60}>
                        <Empty description="Nenhum compilado foi produzido, ainda" />
                    </Centered>
                )}
            </Row>

            <Modal
                centered
                maskClosable={false}
                onCancel={() => setProcessModalOpen(false)}
                open={processModalOpen}
                footer={() => (
                    <Flex justify="space-between">
                        <Button onClick={() => setProcessModalOpen(false)}>
                            Cancelar
                        </Button>

                        <Button
                            disabled={!targetMonths.length}
                            type="primary"
                            onClick={createBatchProcessing}
                            loading={isSubmitting}
                        >
                            Solicitar Compilação
                        </Button>
                    </Flex>
                )}
            >
                <Row style={{ marginBottom: '1rem' }}>
                    <Typography.Title level={5}>
                        Sobre as atividades de quais <b>meses</b>?
                    </Typography.Title>

                    <DatePicker
                        multiple
                        style={{ width: '100%' }}
                        onChange={selectedMonths => {
                            if (!selectedMonths || selectedMonths.length === 0) {
                                setTargetMonths([]);
                                return;
                            }

                            const months = selectedMonths.map(month => 
                                month.format('MM-YYYY')
                            );

                            setTargetMonths(months);
                        }}
                        placeholder="Selecione os meses"
                        picker="month"
                        size="large"
                        format={{
                            format: 'MM/YYYY',
                            type: 'mask'
                        }}
                        disabledDate={current => {
                            const endOfLastMonth = dayjs().subtract(1, 'month').endOf('month');

                            // somente permite a compilação de atividades
                            // dos meses anteriores:
                            return current.isAfter(endOfLastMonth, 'month');
                        }}
                    />
                </Row>

                <Row style={{ marginBottom: '1rem' }}>
                    <Typography.Title level={5}>
                        Sobre quais <b>defensores(as)</b>?
                    </Typography.Title>                   

                    <Space style={{ width: '100%' }} direction="vertical">
                        <Select
                            mode="multiple"
                            allowClear
                            style={{ width: '100%' }}
                            size="large"
                            placeholder="Selecione os defensores"
                            defaultValue={processDefenderIds}
                            value={processDefenderIds}
                            options={defenders.map(defender => ({
                                value: defender.id,
                                label: defender.user.full_name
                            }))}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            onChange={value => {
                                const nextDefenderIds = value.filter(id => id !== 'Todos');

                                setProcessDefenderIds([...new Set(nextDefenderIds)]);
                            }}
                        />
                    </Space>
                </Row>                

                <Alert
                    banner
                    type="info"
                    message={                        
                        <Typography.Text>
                            A compilação dos relatórios pode demorar alguns minutos e serão executadas em fila, pois requer o processamento de vários arquivos.
                        </Typography.Text>
                    }
                />
            </Modal>

            <Modal
                centered
                open={deleteModalOpen}
                onCancel={() => setDeleteModalOpen(false)}
                onOk={() => deleteBatch(deleteModalOpen)}
                title="Apagar Compilado?"
                confirmLoading={isSubmitting}
                cancelText="Não"
                okText="Sim, apague"
                okButtonProps={{ danger: true }}
            >
                {deleteModalOpen && (
                    <>
                        <Typography.Text>
                            Deseja realmente apagar o compilado com as decisões dos RMAs de <b>{deleteModalOpen.date_ref.replace('-', '/')}</b>?
                        </Typography.Text>

                        <Typography.Text>
                            Não se preocupe, você poderá compilar essas decisões novamente, a qualquer momento.
                        </Typography.Text>
                    </>
                )}
            </Modal>
        </>
    );
}
